import React, {useCallback, useEffect, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {AgentApprovalCategory, AgentType, RoutineRunAs} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import * as ChatActions from 'llm/chat-library/ChatActions';
import {
  getAssistantEffortPreference,
  getEffort,
  getDraftState,
  getPublicDataEnabledPreference,
  getSources,
  seedDraftStateFromMessage,
} from 'llm/chat-library/ChatDraftState';
import {getCompletedUploads} from 'llm/chat-library/ChatFileHelper';
import {ChatInput, useChatInputControls} from 'llm/chat-library/ChatInput';
import {getChatTypesForNewAgentChat} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {useMCPEnabledServersOptional} from 'llm/mcp-client-library/MCPEnabledServersContext';
import {getChatUrlPath} from 'llm/routes/UrlUtils';
import notifications from 'notifications';
import * as Net from 'util/Net';

import {runAgentDirectly} from './RoutinesDAO';
import {RoutineQueryData} from './useRoutine';

interface RoutineDmChatInputProps {
  routine: Pick<
    NonNullable<RoutineQueryData['routine']>,
    'id' | 'runAs' | 'allowedApprovalCategories' | 'agentWorkspaceFolder'
  >;
}

/**
 * A stable, non-null draft key so the DM composer reuses the real draft machinery (text, the S3
 * upload flow, web search / connector selection) without colliding with the shared new-chat draft
 * (which is keyed null).
 */
function routineDmDraftKey(routineId: string): string {
  return `routine-dm:${routineId}`;
}

/**
 * Sends the composed draft to the agent as a direct message, then lands the user in the seeded chat.
 *
 * Reuses `getSources` (web search / connectors) and `getCompletedUploads` (uploads) so the DM
 * carries the same selections a normal chat message would. The draft is cleared only on success, so
 * the user's text and files survive a failed send.
 */
function useRoutineDmSend(
  routineId: string,
  draftKey: string,
  runsAsServiceAccount: boolean,
  isFullActionsDisabled: boolean
): {isSubmitting: boolean; sendMessage: () => Promise<void>} {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const mcpEnabledServers = useMCPEnabledServersOptional();
  const mcpServerIds = mcpEnabledServers?.enabledServerIds;

  const sendMessage = useCallback(async () => {
    const draft = getDraftState(draftKey);
    if (draft == null || draft.settings == null) {
      return;
    }
    const {text} = draft.message;
    const sources = getSources(draft.settings, mcpServerIds ?? [], runsAsServiceAccount);
    const fileBlocks = getCompletedUploads(draft.message.selectedFiles);
    setIsSubmitting(true);
    try {
      const {chatSessionId} = await runAgentDirectly(routineId, text, {
        sources,
        fileBlocks,
        initialApprovalState: isFullActionsDisabled
          ? {approvalCategories: [], agentWorkspaceFolderId: null}
          : undefined,
        effort: getEffort(draft.settings) ?? undefined,
      });
      ChatActions.setDraftMessage(draftKey, null);
      // Navigate via the chat URL so the chats/:id route flips the sidepanel slot from the Agent
      // homepage to the chat and fixes the address bar (a back/refresh stays on the chat).
      navigateToURL(getChatUrlPath(chatSessionId));
      // eslint-disable-next-line benchlint/always-throw-from-catch -- terminal UI handler; surface to the user instead of rethrowing
    } catch (error: unknown) {
      notifications.error(Net.getUserMessage(error) ?? 'Could not message this agent. Please try again.', {
        id: 'routine-dm-send-error',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [draftKey, isFullActionsDisabled, mcpServerIds, routineId, runsAsServiceAccount]);

  return {isSubmitting, sendMessage};
}

/**
 * The Agent homepage's "message this agent" composer. Reuses the presentational `ChatInput` with a
 * routine-DM submit that starts a run seeded with the user's message (their message is the request;
 * the agent reads its configured instructions itself). The Full actions chip reflects the agent's
 * preset and can be dismissed for this visit, but cannot be re-enabled from the restricted composer.
 */
export default function RoutineDmChatInput({routine}: RoutineDmChatInputProps): JSX.Element {
  const draftKey = routineDmDraftKey(routine.id);
  const runsAsServiceAccount = routine.runAs === RoutineRunAs.SERVICE_ACCOUNT;
  const hasConfiguredFullActions =
    BenchlingConfig.LLM_ENABLE_AGENT_APPROVALS &&
    (routine.allowedApprovalCategories.includes(AgentApprovalCategory.WRITE_ACTIONS) ||
      routine.agentWorkspaceFolder != null);
  const [isFullActionsDisabled, setIsFullActionsDisabled] = useState(false);

  // Seed ASSISTANT settings so the composer is immediately usable and PlusMenu unlocks web search /
  // skills / connectors (which gate on agentType != null). The type itself is never shown (the
  // selector is hidden by restrictToAgentControls). seedDraftStateFromMessage preserves any existing
  // draft text/files, so a returning user's in-progress message is kept.
  useEffect(() => {
    seedDraftStateFromMessage(draftKey, {
      agentType: AgentType.ASSISTANT,
      publicDataEnabled: getPublicDataEnabledPreference(),
      effort: getAssistantEffortPreference(),
    });
  }, [draftKey]);

  const controls = useChatInputControls(draftKey);
  const {inputText, agentType, hasUploadingFiles} = controls;
  const {isSubmitting, sendMessage} = useRoutineDmSend(
    routine.id,
    draftKey,
    runsAsServiceAccount,
    isFullActionsDisabled
  );

  const handleRevokeFullAccess = useCallback((): Promise<boolean> => {
    setIsFullActionsDisabled(true);
    return Promise.resolve(true);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!inputText.trim() || isSubmitting || hasUploadingFiles || agentType == null) {
      return;
    }
    await sendMessage();
  }, [agentType, hasUploadingFiles, inputText, isSubmitting, sendMessage]);

  // TODO(nweeden): Extend the direct-run endpoint to apply citationGenerationMode, then show Citations here.
  return (
    <ChatInput
      chatSessionId={null}
      {...controls}
      onSubmit={handleSubmit}
      inputDisabled={isSubmitting}
      allowedChatTypes={getChatTypesForNewAgentChat()}
      targetItem={null}
      fullAccessEnabled={hasConfiguredFullActions && !isFullActionsDisabled}
      fullAccessFolderId={isFullActionsDisabled ? null : (routine.agentWorkspaceFolder?.id ?? null)}
      onRevokeFullAccess={handleRevokeFullAccess}
      restrictToAgentControls={true}
      hideCitationsControl={true}
      hideMcpControl={runsAsServiceAccount}
      requireTextForSubmit={true}
    />
  );
}
